import { ChangeEvent, ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { Image, Trash } from 'react-feather';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../Shared/api';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { User } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { FileInput } from '../../Shared/UI/FileInput';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { useRouter } from '../router';
import { Charity } from '../types';
import Layout from '../UI/Layout';

export interface EditMailSettingsFormProps {
  charity: Charity;
  user: User;
}

export default function EditMailSettingsForm({
  charity,
}: EditMailSettingsFormProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      mail_sender: charity.mail_sender,
      mail_header: null,
      delete_mail_header: false,
    },
  });

  const {
    register, setError, handleSubmit, resetField, setValue, watch,
    formState: { defaultValues, errors },
  } = form;

  const [saveMailSettings, { errors: serverErrors, loading }] = useMutation(
    routes.save_mail_settings(),
    {
      preserveState: 'errors',
      onSuccess: () => {
        notifier.notify(t('backend:information_updated'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const [mailHeader, setMailHeader] = useState<File>();
  const deleteMailHeader = watch('delete_mail_header');
  const canDeleteMailHeader = !deleteMailHeader && (!!mailHeader || !!charity.mail_header_url);

  const handleMailHeaderChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMailHeader(event.target.files?.[0] || undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', false);
    setMailHeaderSrc(undefined);
  };

  const unsetMailHeader = () => {
    setMailHeader(undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', true);
    setMailHeaderSrc(undefined);
  };

  const [mailHeaderSrc, setMailHeaderSrc] = useState<string>();

  useEffect(() => {
    if (mailHeader) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setMailHeaderSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(mailHeader);
    }
  }, [mailHeader]);

  const validateMailHeader = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 570) {
      unsetMailHeader();
      setError('mail_header', { message: t('shared:validation.image_too_small') });
    }
  };

  const submit = async (data: typeof defaultValues) => {
    await saveMailSettings({
      ...data,
      mail_header: mailHeader,
      delete_mail_header: deleteMailHeader,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_mail_settings')}
          </h3>
        </div>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} scrollIntoView />}

          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:mail_settings')}
            </h4>
            <div className="text-slate-500">
              {t('backend:default_mail_settings_description')}
            </div>
          </div>

          <InputGroup>
            <InputLabel htmlFor="edit_project_mail_sender">
              {t('backend:mail_sender')}
            </InputLabel>
            <Input
              {...register('mail_sender')}
              id="edit_project_mail_sender"
              aria-invalid={!!errors.mail_sender}
              placeholder={charity.title}
            />
            <ErrorMessage error={errors.mail_sender} attribute={t('backend:mail_sender')} />
          </InputGroup>
          <InputGroup>
            <InputLabel htmlFor="edit_project_header">
              {t('backend:project_mail_header')}
            </InputLabel>
            <InputDescription>
              {t('backend:project_mail_header_description')}
            </InputDescription>
            <div className="max-w-[604px] p-4 border rounded-md border-slate-250">
              <div
                className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50"
              >
                {!mailHeader && !deleteMailHeader && charity.mail_header_url && (
                  <img
                    src={charity.mail_header_url}
                    alt={charity.title}
                    className="w-full rounded-sm"
                  />
                )}
                {mailHeader && mailHeaderSrc && (
                  <img
                    src={mailHeaderSrc}
                    alt={charity.title}
                    className="w-full rounded-sm"
                    onLoad={validateMailHeader}
                  />
                )}
                {((!mailHeader && !charity.mail_header_url) || deleteMailHeader) && (
                  <Icon className="!w-12 !h-12 text-slate-250 my-16">
                    <Image strokeWidth={1} />
                  </Icon>
                )}
              </div>
              <div className="flex space-x-2">
                <FileInput
                  {...register('mail_header')}
                  accept=".jpg,.jpeg,.png"
                  id="edit_project_mail_header"
                  aria-invalid={!!errors.mail_header}
                  onChange={handleMailHeaderChange}
                >
                  <Icon className="mr-1">
                    <Image />
                  </Icon>
                  {t('shared:form.select')}
                </FileInput>
                {canDeleteMailHeader && (
                  <Button onClick={() => unsetMailHeader()} variant="tertiary" size="sm">
                    <Icon className="mr-1">
                      <Trash />
                    </Icon>
                    {t('shared:form.delete')}
                  </Button>
                )}
              </div>
            </div>
            <ErrorMessage error={errors.mail_header} attribute={t('backend:project_mail_header')} />
          </InputGroup>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

EditMailSettingsForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
